/**
 * Admin Shipping Calculator - Manage rates with dimensions and weight
 */
import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  Calculator, Plus, Trash2, Save, RefreshCw, Loader2,
  MapPin, Scale, Box, DollarSign, Truck
} from "lucide-react";

const PARISHES = [
  "Kingston", "St. Andrew", "St. Thomas", "Portland", "St. Mary",
  "St. Ann", "Trelawny", "St. James", "Hanover", "Westmoreland",
  "St. Elizabeth", "Manchester", "Clarendon", "St. Catherine"
];

const AdminShippingCalculator = () => {
  const { isAdmin } = useAuth();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // New rate form
  const [newRate, setNewRate] = useState({
    from_parish: "",
    to_parish: "",
    base_rate: "500",
    weight_rate: "100",
    dimension_rate: "20",
    dimension_threshold: "5000"
  });
  
  // Calculator
  const [calcInput, setCalcInput] = useState({
    from_parish: "Kingston",
    to_parish: "St. Andrew",
    weight_kg: "1",
    length_cm: "",
    width_cm: "",
    height_cm: ""
  });
  const [calcResult, setCalcResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/shipping/rates");
      setRates(response.data || []);
    } catch (error) {
      console.log("No rates yet");
      setRates([]);
    }
    setLoading(false);
  };

  const handleAddRate = async () => {
    if (!newRate.from_parish || !newRate.to_parish) {
      toast.error("Please select both parishes");
      return;
    }
    
    setSaving(true);
    try {
      await api.post("/admin/shipping/rates", {
        from_parish: newRate.from_parish,
        to_parish: newRate.to_parish,
        base_rate: parseFloat(newRate.base_rate),
        weight_rate: parseFloat(newRate.weight_rate),
        dimension_rate: parseFloat(newRate.dimension_rate),
        dimension_threshold: parseFloat(newRate.dimension_threshold)
      });
      toast.success("Shipping rate saved");
      fetchRates();
      setNewRate({ from_parish: "", to_parish: "", base_rate: "500", weight_rate: "100", dimension_rate: "20", dimension_threshold: "5000" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save rate");
    }
    setSaving(false);
  };

  const handleDeleteRate = async (fromParish, toParish) => {
    if (!confirm("Delete this rate?")) return;
    
    try {
      await api.delete(`/admin/shipping/rates/${fromParish}/${toParish}`);
      toast.success("Rate deleted");
      fetchRates();
    } catch (error) {
      toast.error("Failed to delete rate");
    }
  };

  const handleCalculate = async () => {
    if (!calcInput.from_parish || !calcInput.to_parish || !calcInput.weight_kg) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setCalculating(true);
    try {
      const response = await api.post("/admin/shipping/calculate", {
        from_parish: calcInput.from_parish,
        to_parish: calcInput.to_parish,
        weight_kg: parseFloat(calcInput.weight_kg),
        length_cm: calcInput.length_cm ? parseFloat(calcInput.length_cm) : null,
        width_cm: calcInput.width_cm ? parseFloat(calcInput.width_cm) : null,
        height_cm: calcInput.height_cm ? parseFloat(calcInput.height_cm) : null
      });
      setCalcResult(response.data);
    } catch (error) {
      toast.error("Failed to calculate shipping");
    }
    setCalculating(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Calculator className="w-6 h-6" /> Shipping Calculator
            </h1>
            <p className="text-slate-400">Manage shipping rates by parish, weight, and dimensions</p>
          </div>
          <Button variant="outline" onClick={fetchRates}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Rate Calculator */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-primary" /> Calculate Shipping
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>From Parish *</Label>
                  <Select value={calcInput.from_parish} onValueChange={v => setCalcInput({...calcInput, from_parish: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent>{PARISHES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>To Parish *</Label>
                  <Select value={calcInput.to_parish} onValueChange={v => setCalcInput({...calcInput, to_parish: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent>{PARISHES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="flex items-center gap-2"><Scale className="w-4 h-4" /> Weight (kg) *</Label>
                <Input type="number" value={calcInput.weight_kg} onChange={e => setCalcInput({...calcInput, weight_kg: e.target.value})} className="bg-slate-800 border-slate-700" />
              </div>
              
              <div>
                <Label className="flex items-center gap-2"><Box className="w-4 h-4" /> Dimensions (cm) - Optional</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <Input placeholder="Length" type="number" value={calcInput.length_cm} onChange={e => setCalcInput({...calcInput, length_cm: e.target.value})} className="bg-slate-800 border-slate-700" />
                  <Input placeholder="Width" type="number" value={calcInput.width_cm} onChange={e => setCalcInput({...calcInput, width_cm: e.target.value})} className="bg-slate-800 border-slate-700" />
                  <Input placeholder="Height" type="number" value={calcInput.height_cm} onChange={e => setCalcInput({...calcInput, height_cm: e.target.value})} className="bg-slate-800 border-slate-700" />
                </div>
              </div>
              
              <Button onClick={handleCalculate} className="w-full" disabled={calculating}>
                {calculating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
                Calculate
              </Button>
              
              {calcResult && (
                <div className="p-4 bg-slate-800 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Base Rate</span>
                    <span>J${calcResult.base_cost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Weight Cost</span>
                    <span>J${calcResult.weight_cost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Dimension Cost</span>
                    <span>J${calcResult.dimension_cost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t border-slate-700 pt-2">
                    <span>Total</span>
                    <span className="text-primary">J${calcResult.total_cost.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-slate-400">Estimated delivery: {calcResult.estimated_days} day(s)</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add New Rate */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-500" /> Add/Update Rate
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>From Parish</Label>
                  <Select value={newRate.from_parish} onValueChange={v => setNewRate({...newRate, from_parish: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{PARISHES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>To Parish</Label>
                  <Select value={newRate.to_parish} onValueChange={v => setNewRate({...newRate, to_parish: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{PARISHES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Base Rate (J$)</Label>
                  <Input type="number" value={newRate.base_rate} onChange={e => setNewRate({...newRate, base_rate: e.target.value})} className="bg-slate-800 border-slate-700" />
                </div>
                <div>
                  <Label>Weight Rate (J$/kg)</Label>
                  <Input type="number" value={newRate.weight_rate} onChange={e => setNewRate({...newRate, weight_rate: e.target.value})} className="bg-slate-800 border-slate-700" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Dimension Rate (J$/1000cm³)</Label>
                  <Input type="number" value={newRate.dimension_rate} onChange={e => setNewRate({...newRate, dimension_rate: e.target.value})} className="bg-slate-800 border-slate-700" />
                </div>
                <div>
                  <Label>Dimension Threshold (cm³)</Label>
                  <Input type="number" value={newRate.dimension_threshold} onChange={e => setNewRate({...newRate, dimension_threshold: e.target.value})} className="bg-slate-800 border-slate-700" />
                </div>
              </div>
              
              <Button onClick={handleAddRate} className="w-full" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Rate
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Existing Rates */}
        <Card className="mt-6 bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle>Existing Rates ({rates.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : rates.length === 0 ? (
              <p className="text-center text-slate-400 py-8">No rates configured yet. Add your first rate above.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-2">From</th>
                      <th className="text-left p-2">To</th>
                      <th className="text-right p-2">Base</th>
                      <th className="text-right p-2">Weight</th>
                      <th className="text-right p-2">Dimension</th>
                      <th className="text-right p-2">Threshold</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.map((rate, i) => (
                      <tr key={i} className="border-b border-slate-800 hover:bg-slate-800/50">
                        <td className="p-2">{rate.from_parish}</td>
                        <td className="p-2">{rate.to_parish}</td>
                        <td className="p-2 text-right">J${rate.base_rate}</td>
                        <td className="p-2 text-right">J${rate.weight_rate}/kg</td>
                        <td className="p-2 text-right">J${rate.dimension_rate}</td>
                        <td className="p-2 text-right">{rate.dimension_threshold}cm³</td>
                        <td className="p-2 text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteRate(rate.from_parish, rate.to_parish)}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminShippingCalculator;
